import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AgoraRTC from 'agora-rtc-sdk-ng';
import {
  AgoraRTCProvider,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  useJoin,
  usePublish,
  useRemoteUsers,
  RemoteUser,
  LocalUser
} from 'agora-rtc-react';
import {
  Video, VideoOff, Mic, MicOff, PhoneOff, Monitor, MonitorOff,
  MessageCircle, Users, Settings, Maximize2, ChevronLeft,
  Circle as RecordCircle, Square
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const AGORA_APP_ID = process.env.REACT_APP_AGORA_APP_ID || import.meta.env.REACT_APP_AGORA_APP_ID;

const VideoCallContent = () => {
  const { channelName } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Video call state
  const [token, setToken] = useState('');
  const [uid, setUid] = useState(0);
  const [joined, setJoined] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Feature access state
  const [hasRecordingAccess, setHasRecordingAccess] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [waitingForAdmission, setWaitingForAdmission] = useState(false);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [waitingUsers, setWaitingUsers] = useState([]);
  const [isHost, setIsHost] = useState(false);
  
  // Controls state
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  // Agora hooks
  const { localCameraTrack, isLoading: isCameraLoading } = useLocalCameraTrack(isCameraOn);
  const { localMicrophoneTrack, isLoading: isMicLoading } = useLocalMicrophoneTrack(isMicOn);
  const remoteUsers = useRemoteUsers();
  
  // Log when remote users change
  useEffect(() => {
    console.log('Remote users updated:', remoteUsers.length, 'users:', remoteUsers.map(u => ({
      uid: u.uid,
      hasVideo: u.hasVideo,
      hasAudio: u.hasAudio
    })));
  }, [remoteUsers]);
  
  // Log when tracks are ready
  useEffect(() => {
    console.log('Local tracks status:', {
      camera: localCameraTrack ? 'ready' : 'not ready',
      cameraLoading: isCameraLoading,
      mic: localMicrophoneTrack ? 'ready' : 'not ready',
      micLoading: isMicLoading
    });
  }, [localCameraTrack, localMicrophoneTrack, isCameraLoading, isMicLoading]);
  
  // Join channel - only when token is ready
  useJoin(
    {
      appid: AGORA_APP_ID,
      channel: channelName || 'test-channel',
      token: token || null,
      uid: uid
    },
    joined && token !== '' // Only join when we have a token
  );
  
  // Publish local tracks - only when joined and tracks are ready
  usePublish([localMicrophoneTrack, localCameraTrack]);

  // Check access and generate token
  useEffect(() => {
    const initializeCall = async () => {
      try {
        setLoading(true);
        const authToken = localStorage.getItem('auth_token');
        
        console.log('=== VIDEO CALL DEBUG ===');
        console.log('Channel from URL:', channelName);
        
        if (!authToken) {
          alert('Please login to join video call');
          navigate('/login');
          return;
        }

        // Check access
        const accessResponse = await fetch(`${BACKEND_URL}/api/video-calls/check-access`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        const accessData = await accessResponse.json();
        console.log('Access check response:', accessData);
        
        // Check if user is host/moderator
        setIsHost(accessData.role === 'admin' || accessData.subscription_tier === 'crown');
        
        // TEMPORARY: Access check disabled - all users allowed
        // TODO: Re-enable subscription checks later
        // if (!accessData.has_video_access) {
        //   alert('Video calling is only available for Seekers Path and Crown subscribers');
        //   navigate('/community-circles');
        //   return;
        // }

        setHasRecordingAccess(accessData.has_recording);

        // Request to join call and check admission status
        console.log('Requesting to join call for channel:', channelName);
        const joinResponse = await fetch(`${BACKEND_URL}/api/video-calls/join-call/${channelName}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        });

        if (!joinResponse.ok) {
          throw new Error('Failed to join call');
        }

        const joinData = await joinResponse.json();
        console.log('Join call response:', joinData);
        
        // Set host status based on join response
        if (joinData.is_host || joinData.status === 'admitted_as_host') {
          setIsHost(true);
        }
        
        if (joinData.status === 'waiting') {
          // User is in waiting room
          setWaitingForAdmission(true);
          setLoading(false);
          return;
        }

        // User is admitted, generate token
        console.log('Generating token for channel:', joinData.channel_name || channelName);
        const tokenResponse = await fetch(`${BACKEND_URL}/api/video-calls/generate-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            channel_name: joinData.channel_name || channelName, // Use channel_name from join response
            uid: 0,
            role: 1
          })
        });

        if (!tokenResponse.ok) {
          throw new Error('Failed to generate token');
        }

        const tokenData = await tokenResponse.json();
        console.log('✅ Token generated successfully:', {
          channel: tokenData.channel,
          uid: tokenData.uid,
          appId: tokenData.app_id,
          channelFromURL: channelName,
          tokenLength: tokenData.token.length
        });
        
        setToken(tokenData.token);
        setUid(tokenData.uid);
        setLoading(false);
        
        // Set joined AFTER token is ready to ensure proper initialization
        setTimeout(() => {
          console.log('🎥 Joining Agora channel:', tokenData.channel, 'with UID:', tokenData.uid);
          setJoined(true);
        }, 100);
      } catch (error) {
        console.error('Error initializing video call:', error);
        alert('Failed to join video call. Please try again.');
        navigate('/');
      }
    };

    initializeCall();
  }, [channelName, navigate]);

  // Poll for admission status when waiting
  useEffect(() => {
    if (!waitingForAdmission) return;

    const pollAdmission = async () => {
      try {
        const authToken = localStorage.getItem('auth_token');
        const response = await fetch(`${BACKEND_URL}/api/video-calls/check-admission/${channelName}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === 'admitted') {
            setWaitingForAdmission(false);
            // Generate token and join
            const tokenResponse = await fetch(`${BACKEND_URL}/api/video-calls/generate-token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({
                channel_name: data.channel_name || channelName, // Use channel_name from admission response
                uid: 0,
                role: 1
              })
            });

            if (tokenResponse.ok) {
              const tokenData = await tokenResponse.json();
              setToken(tokenData.token);
              setUid(tokenData.uid);
              setLoading(false);
              
              setTimeout(() => {
                setJoined(true);
              }, 100);
            }
          }
        }
      } catch (error) {
        console.error('Error checking admission:', error);
      }
    };

    const interval = setInterval(pollAdmission, 2000); // Poll every 2 seconds
    return () => clearInterval(interval);
  }, [waitingForAdmission, channelName]);

  // Poll for waiting users (for hosts/moderators)
  useEffect(() => {
    if (!joined || !isHost) return;

    const pollWaitingUsers = async () => {
      try {
        const authToken = localStorage.getItem('auth_token');
        const response = await fetch(`${BACKEND_URL}/api/video-calls/call-status/${channelName}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.is_active && data.waiting_users) {
            setWaitingUsers(data.waiting_users);
          }
        }
      } catch (error) {
        console.error('Error fetching waiting users:', error);
      }
    };

    // Poll immediately and then every 3 seconds
    pollWaitingUsers();
    const interval = setInterval(pollWaitingUsers, 3000);
    return () => clearInterval(interval);
  }, [joined, isHost, channelName]);

  // Toggle camera
  const toggleCamera = async () => {
    if (localCameraTrack) {
      await localCameraTrack.setEnabled(!isCameraOn);
      setIsCameraOn(!isCameraOn);
    }
  };

  // Toggle microphone
  const toggleMic = async () => {
    if (localMicrophoneTrack) {
      await localMicrophoneTrack.setEnabled(!isMicOn);
      setIsMicOn(!isMicOn);
    }
  };

  // Toggle screen sharing
  const toggleScreenShare = async () => {
    try {
      if (!isScreenSharing) {
        // Start screen sharing
        const screenTrack = await AgoraRTC.createScreenVideoTrack();
        if (localCameraTrack) {
          await localCameraTrack.setEnabled(false);
        }
        // Publish screen track
        await AgoraRTC.publish(screenTrack);
        setIsScreenSharing(true);
      } else {
        // Stop screen sharing
        if (localCameraTrack) {
          await localCameraTrack.setEnabled(true);
        }
        setIsScreenSharing(false);
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
      alert('Failed to share screen');
    }
  };

  // Admit user from waiting room
  const admitUser = async (userId) => {
    try {
      const authToken = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/video-calls/admit-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          circle_id: channelName,
          user_id: userId
        })
      });

      if (response.ok) {
        console.log('User admitted:', userId);
        // Remove from waiting list
        setWaitingUsers(prev => prev.filter(u => u.user_id !== userId));
      }
    } catch (error) {
      console.error('Error admitting user:', error);
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      const authToken = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/video-calls/start-recording`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          channel_name: channelName,
          uid: uid
        })
      });

      if (response.ok) {
        setIsRecording(true);
        alert('Recording started');
      } else {
        throw new Error('Failed to start recording');
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording');
    }
  };

  // Stop recording
  const stopRecording = async () => {
    try {
      const authToken = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/video-calls/stop-recording`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          channel_name: channelName,
          uid: uid
        })
      });

      if (response.ok) {
        setIsRecording(false);
        alert('Recording stopped');
      } else {
        throw new Error('Failed to stop recording');
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
      alert('Failed to stop recording');
    }
  };

  // Leave call
  const leaveCall = () => {
    setJoined(false);
    if (localCameraTrack) localCameraTrack.close();
    if (localMicrophoneTrack) localMicrophoneTrack.close();
    navigate('/');
  };

  // Send chat message
  const sendChatMessage = () => {
    if (newMessage.trim()) {
      const message = {
        id: Date.now(),
        user: user?.name || 'Anonymous',
        text: newMessage,
        timestamp: new Date().toLocaleTimeString()
      };
      setChatMessages([...chatMessages, message]);
      setNewMessage('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Connecting to video call...</div>
      </div>
    );
  }

  if (waitingForAdmission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <Card className="bg-slate-800 border-slate-700 p-8 max-w-md mx-auto text-center">
          <div className="text-white mb-4">
            <Users className="w-16 h-16 mx-auto mb-4 text-blue-400" />
            <h2 className="text-2xl font-bold mb-2">Waiting for Admission</h2>
            <p className="text-slate-300 mb-6">
              You're in the waiting room. The host will admit you shortly.
            </p>
            <div className="flex justify-center space-x-4">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-700"
              >
                Leave Waiting Room
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-slate-800/90 backdrop-blur-sm px-6 py-4 flex items-center justify-between border-b border-slate-700">
        <div className="flex items-center gap-4">
          <Button
            onClick={leaveCall}
            variant="ghost"
            className="text-white hover:bg-slate-700"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-white">
            {channelName || 'Video Call'}
          </h1>
          {isRecording && (
            <div className="flex items-center gap-2 text-red-400">
              <RecordCircle className="w-4 h-4 animate-pulse fill-current" />
              <span className="text-sm">Recording</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowParticipants(!showParticipants)}
            variant="ghost"
            className="text-white hover:bg-slate-700"
          >
            <Users className="w-5 h-5 mr-2" />
            {remoteUsers.length + 1}
          </Button>
          
          {/* Waiting Room Button - Only visible to host */}
          {isHost && waitingUsers.length > 0 && (
            <Button
              onClick={() => setShowWaitingRoom(!showWaitingRoom)}
              variant="ghost"
              className="text-white hover:bg-slate-700 relative"
            >
              <Users className="w-5 h-5 mr-2" />
              Waiting ({waitingUsers.length})
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                {waitingUsers.length}
              </span>
            </Button>
          )}
        </div>
      </div>

      {/* Main Video Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video Grid */}
        <div className="flex-1 p-4 grid gap-4" style={{
          gridTemplateColumns: remoteUsers.length === 0 ? '1fr' : 
                               remoteUsers.length === 1 ? 'repeat(2, 1fr)' :
                               remoteUsers.length <= 4 ? 'repeat(2, 1fr)' :
                               'repeat(3, 1fr)'
        }}>
          {/* Local User */}
          <div className="relative bg-slate-800 rounded-lg overflow-hidden">
            <LocalUser
              audioTrack={localMicrophoneTrack}
              videoTrack={localCameraTrack}
              cameraOn={isCameraOn}
              micOn={isMicOn}
              playAudio={false}
              playVideo={isCameraOn}
              className="w-full h-full"
            />
            <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm">
              You {!isMicOn && '(muted)'}
            </div>
          </div>

          {/* Remote Users */}
          {remoteUsers.map((remoteUser) => (
            <div key={remoteUser.uid} className="relative bg-slate-800 rounded-lg overflow-hidden">
              <RemoteUser
                user={remoteUser}
                className="w-full h-full"
              />
              <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm">
                User {remoteUser.uid}
              </div>
            </div>
          ))}
        </div>

        {/* Chat Sidebar */}
        {showChat && (
          <Card className="w-80 bg-slate-800 border-slate-700 m-4 flex flex-col">
            <div className="p-4 border-b border-slate-700">
              <h3 className="text-white font-semibold">Chat</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.map((msg) => (
                <div key={msg.id} className="bg-slate-700/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-blue-300 text-sm font-medium">{msg.user}</span>
                    <span className="text-gray-400 text-xs">{msg.timestamp}</span>
                  </div>
                  <p className="text-white text-sm">{msg.text}</p>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-slate-700 flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Type a message..."
                className="bg-slate-700 border-slate-600 text-white"
              />
              <Button onClick={sendChatMessage} className="bg-blue-600 hover:bg-blue-700">
                Send
              </Button>
            </div>
          </Card>
        )}
        
        {/* Waiting Room Sidebar - Only visible to host */}
        {showWaitingRoom && isHost && (
          <Card className="w-80 bg-slate-800 border-slate-700 m-4 flex flex-col">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-white font-semibold">Waiting Room</h3>
              <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                {waitingUsers.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {waitingUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">No one waiting</p>
                </div>
              ) : (
                waitingUsers.map((user) => (
                  <div key={user.user_id} className="bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-3 mb-3">
                      {user.profile_picture ? (
                        <img
                          src={user.profile_picture}
                          alt={user.full_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                          <Users className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{user.full_name}</p>
                        <p className="text-gray-400 text-xs">Waiting to join</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => admitUser(user.user_id)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                    >
                      Admit to Call
                    </Button>
                  </div>
                ))
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Controls Bar */}
      <div className="bg-slate-800/90 backdrop-blur-sm px-6 py-4 border-t border-slate-700">
        <div className="flex items-center justify-center gap-4">
          {/* Camera Toggle */}
          <Button
            onClick={toggleCamera}
            className={`rounded-full w-14 h-14 ${isCameraOn ? 'bg-slate-700 hover:bg-slate-600' : 'bg-red-600 hover:bg-red-700'}`}
          >
            {isCameraOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
          </Button>

          {/* Microphone Toggle */}
          <Button
            onClick={toggleMic}
            className={`rounded-full w-14 h-14 ${isMicOn ? 'bg-slate-700 hover:bg-slate-600' : 'bg-red-600 hover:bg-red-700'}`}
          >
            {isMicOn ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
          </Button>

          {/* Screen Share */}
          <Button
            onClick={toggleScreenShare}
            className={`rounded-full w-14 h-14 ${isScreenSharing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-700 hover:bg-slate-600'}`}
          >
            {isScreenSharing ? <MonitorOff className="w-6 h-6" /> : <Monitor className="w-6 h-6" />}
          </Button>

          {/* Chat Toggle */}
          <Button
            onClick={() => setShowChat(!showChat)}
            className={`rounded-full w-14 h-14 ${showChat ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-700 hover:bg-slate-600'}`}
          >
            <MessageCircle className="w-6 h-6" />
          </Button>

          {/* Recording (Crown/Admin only) */}
          {hasRecordingAccess && (
            <Button
              onClick={isRecording ? stopRecording : startRecording}
              className={`rounded-full w-14 h-14 ${isRecording ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-700 hover:bg-slate-600'}`}
            >
              {isRecording ? <Square className="w-6 h-6" /> : <RecordCircle className="w-6 h-6" />}
            </Button>
          )}

          {/* Leave Call */}
          <Button
            onClick={leaveCall}
            className="rounded-full w-14 h-14 bg-red-600 hover:bg-red-700 ml-4"
          >
            <PhoneOff className="w-6 h-6" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with AgoraRTCProvider
const VideoCall = () => {
  // Create Agora client instance
  const client = useMemo(() => {
    const agoraClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
    
    // Add event listeners for debugging
    agoraClient.on('user-joined', (user) => {
      console.log('User joined channel:', user.uid);
    });
    
    agoraClient.on('user-left', (user) => {
      console.log('User left channel:', user.uid);
    });
    
    agoraClient.on('user-published', (user, mediaType) => {
      console.log('User published:', user.uid, 'mediaType:', mediaType);
    });
    
    agoraClient.on('connection-state-change', (curState, prevState) => {
      console.log('Connection state changed:', prevState, '->', curState);
    });
    
    return agoraClient;
  }, []);

  return (
    <AgoraRTCProvider client={client}>
      <VideoCallContent />
    </AgoraRTCProvider>
  );
};

export default VideoCall;
